import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { SearchX } from 'lucide-react';
import type { ProfileSearchGroup } from '../utils/profileSearch';

/**
 * What the rail's search found, in the panel the picker would otherwise fill.
 *
 * **One card per SECTION, which is what makes the result readable.** A flat list of rows from
 * fourteen tables answers "is it in this file" and not "where is it", and where is the
 * question somebody searching a 201 file actually has — they are going to open that section
 * next. The card's title is the section's own name, straight from the nav, so the result and
 * the rail cannot call one section two things.
 *
 * **No Edit, and that is one of the listed reasons rather than an omission**: this is a
 * DERIVED VIEW of fields other cards edit. Its cog carries the one thing it can do — open the
 * section the rows came from.
 */
export function ProfileSearchPanel({
  term,
  groups,
  total,
  labelOf,
  onOpenPanel,
}: {
  /** What was typed, for the empty state to quote back. */
  term: string;
  groups: ProfileSearchGroup[];
  total: number;
  /** The section's name as the RAIL spells it — `findNavLabel`, never a second table here. */
  labelOf: (panel: string) => string;
  /** Opens a section and leaves the search, which is what every result is for. */
  onOpenPanel: (panel: string) => void;
}) {
  return (
    <ProfileSection
      id="personnel-search-results"
      count={total}
      description={
        /*
          THE LIMIT, stated on the surface rather than in a comment nobody reading the screen
          will see. A reader who searches a relative's name and gets nothing must be told the
          search could never have found it — otherwise the honest answer ("no row matched")
          reads as "this employee has no such relative", which is a different and wrong fact.
        */
        'Every section of this 201 file, searched on the values stored in it. Names are not ' +
        'searched: a relative, a referee and an emergency contact are recorded by reference, ' +
        'and their names are resolved only when their own section is opened.'
      }
    >
      {groups.length === 0 ? (
        <NextActionAlert
          icon={<SearchX className="h-4 w-4" aria-hidden="true" />}
          title={`Nothing in this record matches “${term}”`}
          description="Try a shorter term, or open the section you expect it in — a name is not searched here."
        />
      ) : (
        groups.map((group) => {
          const label = labelOf(group.panel);

          return (
            <ProfileCard
              key={group.panel}
              id={`personnel-search-${group.panel}`}
              title={label}
              count={group.hits.length}
              menuItems={[
                {
                  key: 'open',
                  label: `Open ${label}`,
                  onClick: () => onOpenPanel(group.panel),
                },
              ]}
            >
              {/*
                The row's own identity on the left and WHY it matched on the right — the
                reason a result is not simply the panel again. A row matching on several
                fields states each of them: dropping all but the first would hide the one the
                reader was actually looking for whenever two fields carry the same word.
              */}
              <PreviewTable
                variant="table-display"
                rows={group.hits.map((hit) => ({
                  label: hit.deleted ? `${hit.title} (deleted)` : hit.title,
                  value: (
                    <span className="flex flex-col gap-0.5">
                      {hit.matches.map((match) => (
                        <span key={match.label}>
                          <span className="text-slate-400">{match.label}: </span>
                          {match.value}
                        </span>
                      ))}
                    </span>
                  ),
                }))}
              />
            </ProfileCard>
          );
        })
      )}
    </ProfileSection>
  );
}
